import { useCallback, useMemo, useState } from 'react';
import { useExpanded, useFlexLayout, useRowSelect, useTable } from 'react-table';
import { useDispatch, useSelector } from 'react-redux';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { USD } from '../../../../../../../constants/currency-codes';

import { entitiesBulkload } from '../../../../../../../store/entities/entities.actions';
import { processEntities } from '../../../../../../../store/entities/entities.bulkload';
import { useEntitiesSalesOrder } from '../../../../../../../hooks/entities/selectors/sales-orders';
import { updateFulfillmentPlansAmountSummary } from '../../../../../../../fulfillment-plans/fulfillment-plans.api';
import Global from '../../../../../../../store/global-selectors';
import {
  productCostColumnId,
  productCostColumnDefinition,
  shipmentColumnId,
  shipmentColumnDefinition,
  shippingPaidColumnId,
  shippingPaidColumnDefinition,
  taxPaidColumnId,
  taxPaidColumnDefinition,
  totalPaidColumnId,
  totalPaidColumnDefinition,
  totalColumnId,
  totalColumnDefinition,
  dutiesPaidColumnId,
  dutiesPaidColumnDefinition,
} from './columns/index';
import { createLookupHash } from '../../../../../../../services/hash-service';
import {
  calculateProductAmount,
  cleanAmountSummary,
  calculateTotalAmount,
} from '../../../../../../../fulfillment-plans/amount-summary/helper';
import { CountryConfigNames } from '../../../../../../../contexts/country-config-context/country-config-enums';
import { useCountryConfig } from '../../../../../../../hooks/use-country-config';
import { useEntitiesFulfillmentPlansFromFulfillmentPlanIds } from '../../../../../../../hooks/entities/selectors/fulfillment-plans';
import { useDutiesInAmountSummary } from '../../../../../../../feature-flags/hooks/use-duties-in-amount-summary';

const columns = [
  shipmentColumnDefinition(),
  productCostColumnDefinition(),
  shippingPaidColumnDefinition(),
  taxPaidColumnDefinition(),
  dutiesPaidColumnDefinition(),
  totalColumnDefinition(),
  totalPaidColumnDefinition(),
];

export const currencyDataBuilder = (currencyValue = emptyObject, disabled = false) => {
  const { value, code } = cleanAmountSummary(currencyValue);
  return {
    value,
    code,
    disabled,
  };
};

export const useCreateAmountSummary = (fulfillmentPlanId, amountSummary = emptyObject, items = emptyArray, isTaxesIncluded = false) => {
  const salesOrders = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: [fulfillmentPlanId],
    }),
  );
  const salesOrderItems = createLookupHash(
    salesOrders.reduce((acc, so) => {
      const { items: soItems } = so;
      return [...acc, ...soItems];
    }, []),
    'salesOrderItemId',
  );
  const defaultCurrencyCode = amountSummary?.totalPaid?.code || USD;
  const productAmount = cleanAmountSummary({ value: calculateProductAmount(items, salesOrderItems), code: defaultCurrencyCode });
  const shippingAmount = cleanAmountSummary({ value: amountSummary?.shipping?.value, code: amountSummary?.shipping?.code });
  const taxAmount = cleanAmountSummary({ value: amountSummary?.tax?.value, code: amountSummary?.tax?.code });
  const totalAmount = cleanAmountSummary({
    value: calculateTotalAmount(productAmount?.value, shippingAmount?.value, taxAmount?.value, isTaxesIncluded),
    code: defaultCurrencyCode,
  });
  const totalPaidAmount = cleanAmountSummary({ value: amountSummary?.totalPaid?.value, code: amountSummary?.totalPaid?.code });
  const dutiesTotalAmount = cleanAmountSummary({
    value: amountSummary?.dutiesTotal?.value ?? 0,
    code: amountSummary?.dutiesTotal?.code ?? defaultCurrencyCode,
  });
  const isDutiesTotalNull = amountSummary?.dutiesTotal === null || amountSummary?.dutiesTotal === undefined;

  return {
    productAmount,
    shippingAmount,
    taxAmount,
    totalAmount,
    totalPaidAmount,
    dutiesTotalAmount,
    isDutiesTotalNull,
  };
};

const getSalesOrderAmountSummary = (
  amountSummary,
  salesOrderId,
  includesDuties = true,
  isDutiesInAmountSummaryEnabled = false,
  hasNonNullDutiesTotal = true,
) => {
  const rowData = {
    id: salesOrderId,
    [productCostColumnId]: currencyDataBuilder(amountSummary.productTotal),
    [shipmentColumnId]: 0,
    [shippingPaidColumnId]: currencyDataBuilder(amountSummary.shippingPaid, true),
    [taxPaidColumnId]: currencyDataBuilder(amountSummary.taxPaid, true),
    [totalColumnId]: currencyDataBuilder(amountSummary.orderTotal),
    [totalPaidColumnId]: currencyDataBuilder(amountSummary.totalPaid, true),
  };

  if (includesDuties && hasNonNullDutiesTotal) {
    rowData[dutiesPaidColumnId] = isDutiesInAmountSummaryEnabled
      ? currencyDataBuilder({
          value: amountSummary.dutiesTotalAmount?.value ?? 0,
          code: amountSummary.dutiesTotalAmount?.code ?? USD,
        })
      : currencyDataBuilder({
          value: 0,
          code: amountSummary.totalPaid?.code || USD,
        });
  }

  return [rowData];
};

const getTotalAmount = (newValue, initialTotal, productAmount, defaultCurrencyCode, isTaxesIncluded) => {
  // if new total then take that otherwise take initial total
  if (Object.keys(newValue).length !== 0 && newValue[shippingPaidColumnId] && newValue[taxPaidColumnId]) {
    return cleanAmountSummary({
      value: calculateTotalAmount(
        productAmount.value,
        newValue[shippingPaidColumnId].value,
        newValue[taxPaidColumnId].value,
        isTaxesIncluded,
      ),
      code: defaultCurrencyCode,
    });
  }
  return cleanAmountSummary({
    value: calculateTotalAmount(productAmount.value, initialTotal.shipping?.value || 0, initialTotal.tax?.value || 0, isTaxesIncluded),
    code: defaultCurrencyCode,
  });
};

export const useAmountSummaryTable = (salesOrderId) => {
  const dispatch = useDispatch();
  const [isTaxesIncludedInOrdersTotal] = useCountryConfig(CountryConfigNames.TaxesIncludedInOrdersTotal);
  const isDutiesInAmountSummaryEnabled = useDutiesInAmountSummary();

  const { fulfillmentPlanIds = emptyArray, amountSummary: salesOrderAmountSummary = emptyObject } = useEntitiesSalesOrder(salesOrderId);
  const fulfillmentPlans = useEntitiesFulfillmentPlansFromFulfillmentPlanIds(fulfillmentPlanIds);
  const [formattedAmountSummary, setFormattedAmountSummary] = useState(emptyArray);
  const [isError, setIsError] = useState(false);

  // Check if any fulfillment plan is international
  const hasInternationalShipment = useMemo(() => {
    return fulfillmentPlans.some(({ international }) => international?.isInternational === true);
  }, [fulfillmentPlans]);

  // Check if any fulfillment plan has non-null dutiesTotal
  const hasNonNullDutiesTotal = useMemo(() => {
    return fulfillmentPlans.some(
      ({ amountSummary }) =>
        amountSummary?.dutiesTotal !== null && amountSummary?.dutiesTotal !== undefined && amountSummary?.dutiesTotal.value > 0,
    );
  }, [fulfillmentPlans]);

  const dynamicColumns = useMemo(() => {
    return hasInternationalShipment && isDutiesInAmountSummaryEnabled && hasNonNullDutiesTotal
      ? columns
      : columns.filter((column) => column.accessor !== dutiesPaidColumnId);
  }, [hasInternationalShipment, isDutiesInAmountSummaryEnabled, hasNonNullDutiesTotal]);

  const formattedItems = useMemo(() => {
    const formattedSalesOrderAmountSummary = getSalesOrderAmountSummary(
      salesOrderAmountSummary,
      salesOrderId,
      hasInternationalShipment && isDutiesInAmountSummaryEnabled,
      isDutiesInAmountSummaryEnabled,
      hasNonNullDutiesTotal,
    );
    const formattedFulfillmentPlansAmountSummary = fulfillmentPlans.map(
      ({ fulfillmentPlanId, amountSummary: fulfillmentPlanAmountSummary = emptyObject, items = emptyArray, international }, index) => {
        const defaultCurrencyCode = fulfillmentPlanAmountSummary.totalPaid?.code;
        const addDutiesTotal = international?.isInternational || false;

        const newValue = (formattedAmountSummary && formattedAmountSummary[fulfillmentPlanId]) || emptyObject;

        const productAmount = cleanAmountSummary({
          value: calculateProductAmount(items),
          code: defaultCurrencyCode,
        });
        const totalAmount = getTotalAmount(
          newValue,
          fulfillmentPlanAmountSummary,
          productAmount,
          defaultCurrencyCode,
          isTaxesIncludedInOrdersTotal,
        );

        const rowData = {
          id: fulfillmentPlanId,
          [productCostColumnId]: currencyDataBuilder(productAmount),
          [shipmentColumnId]: index + 1,
          [shippingPaidColumnId]: currencyDataBuilder(fulfillmentPlanAmountSummary.shipping, false),
          [taxPaidColumnId]: currencyDataBuilder(fulfillmentPlanAmountSummary.tax, false),
          [totalColumnId]: currencyDataBuilder(totalAmount),
          [totalPaidColumnId]: currencyDataBuilder(fulfillmentPlanAmountSummary.totalPaid, false),
        };

        if (hasInternationalShipment && isDutiesInAmountSummaryEnabled && hasNonNullDutiesTotal) {
          rowData[dutiesPaidColumnId] = addDutiesTotal
            ? currencyDataBuilder({
                value: fulfillmentPlanAmountSummary.dutiesTotal?.value ?? 0,
                code: fulfillmentPlanAmountSummary.dutiesTotal?.code ?? defaultCurrencyCode,
              })
            : currencyDataBuilder({ value: 0, code: defaultCurrencyCode });
        }

        return rowData;
      },
    );
    return [...formattedFulfillmentPlansAmountSummary, ...formattedSalesOrderAmountSummary];
  }, [
    salesOrderAmountSummary,
    salesOrderId,
    hasInternationalShipment,
    isDutiesInAmountSummaryEnabled,
    fulfillmentPlans,
    formattedAmountSummary,
    isTaxesIncludedInOrdersTotal,
    hasNonNullDutiesTotal,
  ]);

  const updateRowProperty = useCallback((originalData, propertyName, newValue) => {
    if (typeof newValue === 'undefined') {
      return;
    }

    setFormattedAmountSummary((prevState) => {
      const cleanValue = newValue === '' ? 0 : Number(newValue);
      const deepCopy = { ...prevState };
      const id = originalData.id;
      // we did not find edited shipment
      if (!(id in deepCopy)) {
        const updatedColumnData = {
          ...originalData[propertyName],
          value: cleanValue,
        };

        // I want to make an object of objects instead of an array of objects
        const payload = {
          [id]: {
            [shippingPaidColumnId]: { ...originalData[shippingPaidColumnId] },
            [taxPaidColumnId]: { ...originalData[taxPaidColumnId] },
            ...(originalData[dutiesPaidColumnId]
              ? {
                  [dutiesPaidColumnId]: {
                    ...originalData[dutiesPaidColumnId],
                  },
                }
              : {}),
            [totalPaidColumnId]: { ...originalData[totalPaidColumnId] },
            [productCostColumnId]: { ...originalData[productCostColumnId] },
            [propertyName]: updatedColumnData,
          },
        };
        return { ...prevState, ...payload };
      }
      deepCopy[id][propertyName].value = cleanValue;
      return { ...prevState, ...deepCopy };
    });
  }, []);

  const saveAmountSummary = useCallback(
    async (closeModal) => {
      setIsError(false);
      try {
        if (!formattedAmountSummary || Object.keys(formattedAmountSummary).length === 0) {
          closeModal();
          return;
        }

        const formattedRequest = Object.keys(formattedAmountSummary).map((fulfillmentPlanId) => {
          const amountSummaryByFulfillmentPlan = formattedAmountSummary[fulfillmentPlanId];

          const shippingPaid = amountSummaryByFulfillmentPlan[shippingPaidColumnId]?.value ?? 0;
          const taxPaid = amountSummaryByFulfillmentPlan[taxPaidColumnId]?.value ?? 0;
          const dutiesPaid =
            hasInternationalShipment &&
            isDutiesInAmountSummaryEnabled &&
            hasNonNullDutiesTotal &&
            amountSummaryByFulfillmentPlan[dutiesPaidColumnId] &&
            typeof amountSummaryByFulfillmentPlan[dutiesPaidColumnId]?.value === 'number'
              ? amountSummaryByFulfillmentPlan[dutiesPaidColumnId].value
              : 0;
          const totalPaid = amountSummaryByFulfillmentPlan[totalPaidColumnId]?.value ?? 0;
          const orderTotal = calculateTotalAmount(
            amountSummaryByFulfillmentPlan[productCostColumnId]?.value,
            amountSummaryByFulfillmentPlan[shippingPaidColumnId]?.value,
            amountSummaryByFulfillmentPlan[taxPaidColumnId]?.value,
          );

          return {
            fulfillmentPlanId,
            shippingPaid,
            taxPaid,
            dutiesPaid,
            totalPaid,
            orderTotal,
          };
        });

        // if there are changes then hit endpoint
        if (formattedRequest.length > 0) {
          const response = await updateFulfillmentPlansAmountSummary(formattedRequest);
          const { data: updatedEntityFulfillmentPlans } = response;
          dispatch(
            entitiesBulkload(
              processEntities({
                fulfillmentPlans: updatedEntityFulfillmentPlans ?? emptyArray,
              }),
            ),
          );
        }
        closeModal();
      } catch {
        setIsError(true);
      }
    },
    [dispatch, formattedAmountSummary, hasInternationalShipment, isDutiesInAmountSummaryEnabled, hasNonNullDutiesTotal],
  );

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns: dynamicColumns,
      data: formattedItems,
      autoResetSelectedRows: false,
      autoResetExpanded: false,
      updateRowProperty,
    },
    useFlexLayout,
    useExpanded,
    useRowSelect,
  );

  return useMemo(() => {
    return {
      updateRowProperty,
      getTableProps,
      headerGroups,
      getTableBodyProps,
      rows,
      prepareRow,
      saveAmountSummary,
      isError,
    };
  }, [updateRowProperty, getTableProps, headerGroups, getTableBodyProps, rows, prepareRow, saveAmountSummary, isError]);
};
